import API, { graphqlOperation } from '@aws-amplify/api'
import loader from '@ergosense/ergosense-react-common/lib/actions/loader'
import { signCompanyContent, uploadCompanyContent } from '@ergosense/ergosense-react-common/lib/actions/signed'
import { createCompanyContentUrl } from '@ergosense/ergosense-react-common/lib/helpers/'
import axios from 'axios'
import dayjs from 'dayjs'
import { getActiveCompanyId } from 'helpers/'
import bbox from '@turf/bbox'
import * as mutations from '../gql/mutations'

export const ACTION = {
  LOAD_STATE: 'editor-load-state',
  OVERLAY: 'editor-overlay',
  SETTINGS: 'editor-settings',
  OVERLAY_MASK: 'editor-overlay-mask',
  SAVE_STATE: 'editor-save-state',
  SET_JSON: 'editor-set-json',
  UNDO: 'editor-undo',
  EDITOR_ERROR: 'editor-error',
  FLOOR_INFO: 'editor-floor-info',
  SET_SATELITE: 'editor-set-satelite',
  WORKSPACE_ATTRIBUTES: 'editor-workspace-attributes',
  INSERT_WORKSPACE_ATTRIBUTE: 'editor-insert-workspace-attribute'
}

/**
 * Query floor information
 *
 * @type {string}
 */
export const getFloorQuery = `
  query($id: Int!) {
    getFloor(id: $id) {
      id,
      name
    }
  }
`
export const getWorkspaceAttributesQuery = `
 query {
  getWorkspaceAttributes {
    id
    name
  }
 }
`

export const insertWorkspaceAttributesMutation = `
  mutation($name: String) {
    insertWorkspaceAttributes(name: $name)
  }
`

export const LOAD = ACTION

export const loadState = (floorId) => {
  return loader.load(LOAD.LOAD_STATE, async (dispatch, getState) => {
    // Grab the active company ID
    const state = getState()
    const companyId = getActiveCompanyId(state)

    try {
      const floorResult = await API.graphql(graphqlOperation(getFloorQuery, { id: floorId }))
      dispatch({ type: ACTION.FLOOR_INFO, info: floorResult.data.getFloor })
    } catch (e) {
      // Stop editor, might be invalid floor
      return dispatch({ type: ACTION.EDITOR_ERROR, error: e.message })
    }

    // Sign company content, the floor exists
    const signed = await dispatch(signCompanyContent())

    // Create a signed company content URL which refers to the floors
    // geojson file
    const url = createCompanyContentUrl(companyId, `${floorId}.geojson`, { versionKey: dayjs().unix(), ...signed })

    try {
      const content = await axios.get(url)
      dispatch({ type: ACTION.LOAD_STATE, geojson: content.data })
    } catch (e) {
      // Floor plan does not exist yet, not an error
      if (e.response && e.response.status === 403) {
        dispatch({ type: ACTION.LOAD_STATE, geojson: null })
      } else {
        throw e
      }
    }
  })
}

// Property keys that are built-in and should never be registered as custom attributes
const SYSTEM_FIELDS = ['type', 'interactive', 'area', 'capacity', 'isCircle', 'active', 'id']

export const saveState = (floorId) => {
  return loader.load(LOAD.SAVE_STATE, async (dispatch, getState) => {
    const data = getState()['editor']
    const features = JSON.parse(JSON.stringify(data.features || []))
    const areaCache = {}

    // 1. Iterate through features and create any new areas
    for (let feature of features) {
      if (feature.properties && typeof feature.properties.area === 'string' && feature.properties.area.trim() !== '') {
        const areaName = feature.properties.area
        if (areaCache[areaName]) {
          feature.properties.area = areaCache[areaName]
        } else {
          try {
            const result = await API.graphql(graphqlOperation(mutations.createArea, {
              name: areaName,
              floor: parseInt(floorId, 10)
            }))
            const newAreaId = result.data.createArea.id
            areaCache[areaName] = newAreaId
            feature.properties.area = newAreaId
          } catch (e) {
            console.error('Failed to create area before save:', e)
          }
        }
      }
    }

    // 2. Create any new attribute keys that don't exist in the backend yet
    const knownAttributes = new Set((data.attributes || []).map(a => a.name))
    const attributeCache = new Set()
    for (let feature of features) {
      if (!feature.properties) continue
      for (let key of Object.keys(feature.properties)) {
        if (!SYSTEM_FIELDS.includes(key) && !knownAttributes.has(key) && !attributeCache.has(key)) {
          try {
            await API.graphql(graphqlOperation(insertWorkspaceAttributesMutation, { name: key }))
            attributeCache.add(key)
          } catch (e) {
            console.error('Failed to create attribute before save:', e)
          }
        }
      }
    }
    // Refresh attributes in the store after any new ones were created
    if (attributeCache.size > 0) dispatch(getWorkspaceAttributes())

    // 3. Synchronize Redux view with actual IDs so the editor doesn't just display the string anymore
    dispatch(setJson({ type: 'FeatureCollection', features, bbox: data.bbox }))

    // 4. Upload the finalized geometry
    return dispatch(uploadCompanyContent('application/json', {
      type: 'FeatureCollection',
      overlay: data.overlay,
      overlayMask: data.overlayMask,
      settings: data.settings,
      features: features,
      bbox: data.bbox
    }, `${floorId}.geojson`))
  })
}


export const setSatelite = (satelite) => ({ type: ACTION.SET_SATELITE, satelite })

export const setJson = (json) => ({ type: ACTION.SET_JSON, json })

export const setOverlay = (overlay) => ({ type: ACTION.OVERLAY, overlay })

export const setSettings = (obj) => ({ type: ACTION.SETTINGS, settings: obj })

export const getWorkspaceAttributes = () => {
  return loader.load(ACTION.WORKSPACE_ATTRIBUTES, async (dispatch) => {
    try {
      const result = await API.graphql(graphqlOperation(getWorkspaceAttributesQuery))
      if (result.data) {
        dispatch({ type: ACTION.WORKSPACE_ATTRIBUTES, attributes: result.data.getWorkspaceAttributes })
      }
    } catch (error) {
      console.error('Failed to load workspace attributes', error)
      dispatch({ type: ACTION.EDITOR_ERROR, error: error.message })
    }
  })
}

export const insertWorkspaceAttribute = (name) => {
  return loader.load(ACTION.INSERT_WORKSPACE_ATTRIBUTE, async (dispatch) => {
    try {
      await API.graphql(graphqlOperation(insertWorkspaceAttributesMutation, { name }))
      // Refresh the attributes list after a successful insert
      dispatch(getWorkspaceAttributes())
    } catch (error) {
      console.error('Failed to insert workspace attribute', error)
      dispatch({ type: ACTION.EDITOR_ERROR, error: error.message })
    }
  })
}

export const setOverlayMask = (id) => ({ type: ACTION.OVERLAY_MASK, id })

export const undo = () => ({ type: ACTION.UNDO })

export const reducer = (state = {}, action) => {
  switch (action.type) {
    case ACTION.SET_SATELITE:
      return {
        ...state,
        satelite: action.satelite
      }
    case ACTION.FLOOR_INFO:
      return {
        ...state,
        floorName: action.info && action.info.name
      }
    case ACTION.WORKSPACE_ATTRIBUTES:
      return {
        ...state,
        attributes: action.attributes
      }
    case ACTION.EDITOR_ERROR:
      return {
        ...state,
        error: true
      }
    case ACTION.UNDO:
      return {
        ...state,
        features: state.oldFeatures || [],
        oldFeatures: []
      }
    case ACTION.SET_JSON:
      return {
        ...state,
        ...action.json,
        bbox: bbox(action.json),
        oldFeatures: (state.features || [])
      }
    case ACTION.OVERLAY:
      return { ...state, overlay: action.overlay }
    case ACTION.SETTINGS:
      return { ...state, settings: { ...state.settings, ...action.settings } }
    case ACTION.OVERLAY_MASK:
      return { ...state, overlayMask: action.id }
    case ACTION.LOAD_STATE:
      return {
        ...state,
        ...action.geojson,
        oldFeatures: []
      }
    default:
      return state
  }
}
